import { Box, Row, Txt } from "@stenajs-webui/core";
import {
  Banner,
  Label,
  PrimaryButton,
  SecondaryButton,
} from "@stenajs-webui/elements";
import { Window, WindowFooter } from "@stenajs-webui/modal";
import { useCallback, useState } from "react";
import { GapValues } from "@/lib/utils/GapValues";
import { RebateOverview } from "@/gql/graphql";
import { useAuthorizePayment } from "../hooks/UseAuthorizePayment";
import { getExpectedGoal } from "../lib/rebateOverviewUtils";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";

export const AuthorizePayoutModal = ({
  isOpen,
  rebate,
  onDone,
}: {
  isOpen: boolean;
  rebate?: RebateOverview;
  onDone: () => void;
}) => {
  const [authorized, setAuthorized] = useState(false);
  const { authorizePayment, error, loading } = useAuthorizePayment();
  const handleClose = useCallback(() => {
    setAuthorized(false);
    onDone();
  }, [onDone]);
  const goal = getExpectedGoal(rebate);
  const goalId = goal?.id;
  return (
    <Window
      isOpen={isOpen}
      onRequestClose={handleClose}
      headerText="Authorize payout"
      indent={GapValues.TWENTYFOUR}
    >
      <Box
        gap={GapValues.TWENTYEIGHT}
        data-testid={testIdConstants.authorizePayoutModal}
      >
        {authorized && (
          <Banner
            variant="success"
            text="Payout has been successfully authorized."
          />
        )}
        {error && (
          <Banner
            variant="error"
            text={error.message || "Something went wrong."}
          />
        )}
        <Row gap={GapValues.TWENTYEIGHT}>
          <Label text="Agreement">
            <Txt>{rebate?.rebateAgreement?.name ?? "—"}</Txt>
          </Label>
          <Label text="Agreement number">
            <Txt>{rebate?.rebateAgreement?.rebateAgreementNumber ?? "—"}</Txt>
          </Label>
          <Label text="Target">
            <Txt>{rebate?.displayedTarget ?? "—"}</Txt>
          </Label>
          <Label text="Expected">
            <Txt>{goal?.expectedVolume ?? "—"}</Txt>
          </Label>
          <Label text="Achieved">
            <Txt>{rebate?.achieved ?? "—"}</Txt>
          </Label>
          <Label text="Period">
            <Txt>{rebate?.period ?? "—"}</Txt>
          </Label>
          <Label text="Reserved">
            <Txt
              data-testid={testIdConstants.authorizePayoutModalReservedValue}
            >
              {rebate?.reservedAmount ?? "—"}
            </Txt>
          </Label>
        </Row>
        {!authorized && (
          <Txt>
            You are about to authorize a payout although the goal has not been
            achieved. Please confirm that you want to proceed.
          </Txt>
        )}
      </Box>
      <WindowFooter>
        <Row gap={GapValues.SIXTEEN} justifyContent="flex-end">
          <SecondaryButton
            label="Close"
            onClick={handleClose}
            data-testid={testIdConstants.authorizePayoutModalCloseButton}
          />
          {!authorized && (
            <PrimaryButton
              label="Authorize"
              data-testid={testIdConstants.authorizePayoutModalAuthorizeButton}
              disabled={!goalId}
              onClick={async () => {
                if (!goalId) return;
                try {
                  await authorizePayment(goalId);
                  setAuthorized(true);
                } catch {
                  // useAuthorizePayment keeps the error for the banner above
                }
              }}
              loading={loading}
            />
          )}
        </Row>
      </WindowFooter>
    </Window>
  );
};
